import React from "react"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { DateTime } from "luxon"

import {
  DateRangePicker,
  LocalizationProvider,
  MultiInputDateRangeField,
} from "@mui/x-date-pickers-pro"
import { AdapterLuxon } from "@mui/x-date-pickers-pro/AdapterLuxon"

interface FormDateRangeFieldProps {
  disabled?: boolean
  /** Marks both inputs natively required; the browser then blocks a blank submit before the form can explain it */
  required?: boolean
}

interface DateRangeFormValues {
  endDate: string
  startDate: string
}

interface DateRangeTextFieldSlotPropsArgs {
  dateFieldErrorMessage: string | undefined
  periodEndLabel: string
  periodStartLabel: string
  required: boolean
}

const getDateRangeFieldPosition = (
  ownerState: object
): "end" | "start" | undefined => {
  const position = (ownerState as { position?: "end" | "start" }).position
  if (position === "end" || position === "start") {
    return position
  }

  return undefined
}

const parseDateFieldValue = (value: unknown): DateTime | null => {
  if (typeof value !== "string" || value.trim() === "") {
    return null
  }

  const parsedDate = DateTime.fromISO(value, { zone: "utc" })
  return parsedDate.isValid ? parsedDate : null
}

const toDateRangeValue = (
  startDate: unknown,
  endDate: unknown
): [DateTime | null, DateTime | null] => [
  parseDateFieldValue(startDate),
  parseDateFieldValue(endDate),
]

const getDateRangeTextFieldSlotProps =
  ({
    dateFieldErrorMessage,
    periodEndLabel,
    periodStartLabel,
    required,
  }: DateRangeTextFieldSlotPropsArgs) =>
  (ownerState: object) => {
    const position = getDateRangeFieldPosition(ownerState)
    const isEndPosition = position === "end"
    const isStartPosition = position === "start"

    return {
      error: Boolean(dateFieldErrorMessage),
      helperText: isEndPosition ? dateFieldErrorMessage : "",
      label: isStartPosition ? periodStartLabel : periodEndLabel,
      required,
      size: "small" as const,
      sx: { maxWidth: "14rem" },
      variant: "outlined" as const,
    }
  }

const DATE_FIELD_SET_VALUE_OPTIONS = {
  shouldDirty: true,
  shouldValidate: true,
} as const

/** The start and end date inputs of a form with `startDate` and `endDate` fields */
export const FormDateRangeField: React.FC<FormDateRangeFieldProps> = ({
  disabled = false,
  required = true,
}) => {
  const { t } = useTranslation("common")
  const form = useFormContext<DateRangeFormValues>()
  const [startDate, endDate] = form.watch(["startDate", "endDate"])
  const { endDate: endDateError, startDate: startDateError } =
    form.formState.errors

  const handleDateRangeChange = (
    nextRange: readonly [DateTime | null, DateTime | null]
  ) => {
    const [nextStartDate, nextEndDate] = nextRange

    form.setValue(
      "startDate",
      nextStartDate?.toISODate() ?? "",
      DATE_FIELD_SET_VALUE_OPTIONS
    )
    form.setValue(
      "endDate",
      nextEndDate?.toISODate() ?? "",
      DATE_FIELD_SET_VALUE_OPTIONS
    )
  }

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <DateRangePicker
        disabled={disabled}
        format="yyyy-MM-dd"
        onChange={handleDateRangeChange}
        slots={{ field: MultiInputDateRangeField }}
        slotProps={{
          textField: getDateRangeTextFieldSlotProps({
            dateFieldErrorMessage:
              endDateError?.message ?? startDateError?.message,
            periodEndLabel: t("bill.periodEnd"),
            periodStartLabel: t("bill.periodStart"),
            required,
          }),
        }}
        value={toDateRangeValue(startDate, endDate)}
      />
    </LocalizationProvider>
  )
}
